import * as Icons from 'lucide-react';
import PremiumIconChip from './PremiumIconChip';

const STEPS = [
  {
    icon: 'MessageSquare',
    step: '01',
    title: 'Describe your idea',
    desc: 'Type one sentence. \u201cA Stripe-powered marketplace for indie musicians.\u201d That is enough.',
    accent: 'from-cyan-400 to-sky-500',
    glow: 'rgba(0,240,255,0.55)',
  },
  {
    icon: 'Cpu',
    step: '02',
    title: 'AI drafts the plan',
    desc: 'Blueprint, tech stack, data model, build plan, sample code \u2014 rendered in seconds.',
    accent: 'from-teal-300 to-cyan-500',
    glow: 'rgba(45,212,191,0.55)',
  },
  {
    icon: 'Code2',
    step: '03',
    title: 'AI writes the code',
    desc: 'React, FastAPI, MongoDB, integrations, auth, payments. Real, running, yours.',
    accent: 'from-sky-400 to-indigo-500',
    glow: 'rgba(99,102,241,0.55)',
  },
  {
    icon: 'Rocket',
    step: '04',
    title: 'Ship to the world',
    desc: 'One-click deploy to a global edge. Custom domain, SSL, CDN &mdash; ready in a minute.',
    accent: 'from-fuchsia-400 to-sky-500',
    glow: 'rgba(232,121,249,0.55)',
  },
];

export default function HowItWorks() {
  return (
    <section
      className="relative py-24 md:py-28 text-white"
      style={{
        background:
          'radial-gradient(1000px 600px at 50% 0%, rgba(0,240,255,0.08) 0%, transparent 55%),' +
          'linear-gradient(180deg, #000 0%, #030303 100%)',
      }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.4em] text-[#00F0FF] font-semibold uppercase mb-4 font-mono">
            <Icons.Workflow size={12} />
            How it works
          </div>
          <h2 className="font-display text-4xl md:text-6xl font-black tracking-[-0.03em] text-white">
            From idea to production in{' '}
            <span className="italic bg-clip-text text-transparent bg-gradient-to-r from-[#00F0FF] to-[#FF007F]">
              four moves.
            </span>
          </h2>
          <p className="mt-5 text-white/60 text-lg leading-relaxed">
            Around the World AI collapses the entire dev cycle into a single
            conversation. No context switching. No boilerplate. Just build.
          </p>
        </div>

        <div className="relative mt-16">
          {/* Connector line */}
          <div className="absolute top-16 left-8 right-8 h-px bg-gradient-to-r from-transparent via-[#00F0FF]/40 to-transparent hidden lg:block" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s) => {
              const Icon = Icons[s.icon] || Icons.Sparkles;
              return (
                <div key={s.step} className="relative group">
                  {/* Node dot on connector */}
                  <div className="hidden lg:block absolute top-16 left-1/2 -translate-x-1/2 -translate-y-1/2">
                    <div className={`h-3 w-3 rounded-full bg-gradient-to-br ${s.accent} shadow-[0_0_20px_rgba(0,240,255,0.7)]`} />
                  </div>

                  <div className="glass rounded-2xl p-6 h-full transition duration-300 hover:-translate-y-1 hover:border-white/25 hover:shadow-[0_25px_60px_-25px_rgba(0,240,255,0.35)]">
                    <div className="flex items-center justify-between">
                      <PremiumIconChip Icon={Icon} gradient={s.accent} glow={s.glow} size={52} iconSize={22} />
                      <span className="font-mono text-[10px] font-semibold tracking-[0.3em] text-white/35">
                        STEP {s.step}
                      </span>
                    </div>
                    <div className="mt-5 font-display font-bold text-white text-lg tracking-tight">
                      {s.title}
                    </div>
                    <div
                      className="mt-2 text-sm text-white/65 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: s.desc }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
